// Global Instructions Rule Applied!
import React, { useState, useEffect, useCallback } from 'react'
import { Row, Col } from 'antd'
import { DealTeam } from '../parts/DealTeam.tsx'
import { DealOverviewSection } from '../sections/DealOverviewSection.tsx'
import { CompanySection } from '../sections/CompanySection.tsx'
import { RoundInformationSection } from '../sections/RoundInformationSection.tsx'
import { BusinessAddressSection } from '../sections/BusinessAddressSection.tsx'
import { RegisteredOfficeSection } from '../sections/RegisteredOfficeSection.tsx'
import { CompanyTeamTable } from '../../../../../core/components/CompanyTeamTable/index.tsx'
import { DealFields } from '../../../../../state_manager/data_models/deal_record.ts'
import { UserEntryFields } from '../../../../../state_manager/data_models/user_entry.ts'
import { UNKNOWN_CONTACT } from '../../../../../state_manager/constants/strings.ts'
import { useDispatch } from 'react-redux'
import { updateDealRecord } from '../../../../../state_manager/state_update_actions/deals/deal_actions.ts'
import { fetchDealById } from '../../../services/dealsService.ts'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import {
  setActiveRecord,
  registerRecordUsage
} from '../../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'

interface DetailTabProps {
  dealTeam?: any[]
  dealRecord?: any
  handleFieldChange?: (key: string, value: any) => void
  onPatchSuccess?: (patchRecordId: string, updates: Record<string, any>) => void
  showIsFundingRound?: boolean
  showRoundDetails?: boolean
  showCompanySection?: boolean
  showDealTeam?: boolean
  recordId?: string | number | null
  commitments?: any[]
  handleCommitmentChange?: (commitments: any[]) => void
  getCompanyDetailsById?: (id: string) => Promise<any> | any
  onCompanyDetailsUpdate?: () => void
  companyTeamMembers?: any[]
  companyTeamMembersKey?: string
  onCompanyTeamMembersChange?: (members: any[]) => void
}

export function DetailTab({
  dealRecord,
  handleFieldChange,
  onPatchSuccess,
  showDealTeam = true,
  recordId,
  commitments = [],
  handleCommitmentChange,
  getCompanyDetailsById,
  companyTeamMembers: propCompanyTeamMembers,
  companyTeamMembersKey,
  onCompanyTeamMembersChange
}: DetailTabProps) {
  const [selectedContacts, setSelectedContacts] = useState<{
    introduced_by: any[]
    sourced_by: any[]
    deal_team_lead: any[]
  }>({
    introduced_by: [],
    sourced_by: [],
    deal_team_lead: []
  })

  const dispatch = useDispatch()

  const companyTeamMembers = propCompanyTeamMembers || []

  const handleTeamTableChange = useCallback(
    async (updatedCompanyTeamMembers: any[]) => {
      const currentToken = getAccessToken(store.getState().auth)

      if (!currentToken) {
        if (onCompanyTeamMembersChange) {
          onCompanyTeamMembersChange(updatedCompanyTeamMembers)
        }
        return
      }

      try {
        if (recordId && recordId !== 'new') {
          const dealResponse = await fetchDealById(recordId, currentToken)
          if (dealResponse.data && !dealResponse.error) {
            const fetchedDeal = dealResponse.data
            const recordIdString = recordId.toString()

            dispatch(setActiveRecord({ uuid: recordIdString, record: fetchedDeal }))
            dispatch(registerRecordUsage({ uuid: recordIdString, context: 'fullForm' }))
            dispatch(updateDealRecord({ dealId: recordIdString, updates: fetchedDeal }))
          }
        }
      } catch (error) {
        console.error('Error refreshing deal data after team table change:', error)
        if (onCompanyTeamMembersChange) {
          onCompanyTeamMembersChange(updatedCompanyTeamMembers)
        }
      }
    },
    [dispatch, onCompanyTeamMembersChange, recordId]
  )

  useEffect(() => {
    if (dealRecord) {
      const contactsByRole: {
        introduced_by: any[]
        sourced_by: any[]
        deal_team_lead: any[]
      } = {
        introduced_by: [],
        sourced_by: [],
        deal_team_lead: []
      }

      const introducedBy = dealRecord[DealFields.contact_accounts__introduced_by] || dealRecord.introduced_by_detail
      if (introducedBy && Array.isArray(introducedBy)) {
        contactsByRole.introduced_by = introducedBy.map((contact: any) => ({
          value: contact[UserEntryFields.contacts__id] || '',
          label:
            contact[UserEntryFields.prog__display_name] ||
            `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
            contact[UserEntryFields.contacts__email] ||
            UNKNOWN_CONTACT,
          email: contact[UserEntryFields.contacts__email] || '',
          data: contact
        }))
      }

      const sourcedBy = dealRecord[DealFields.contact_accounts__sourced_by] || dealRecord.sourced_by_details
      if (sourcedBy && Array.isArray(sourcedBy)) {
        contactsByRole.sourced_by = sourcedBy.map((contact: any) => ({
          value: contact[UserEntryFields.contacts__id] || '',
          label:
            contact[UserEntryFields.prog__display_name] ||
            `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
            contact[UserEntryFields.contacts__email] ||
            UNKNOWN_CONTACT,
          email: contact[UserEntryFields.contacts__email] || '',
          data: contact
        }))
      }

      const lead = dealRecord[DealFields.contact_deals__lead] || dealRecord.lead_detail
      if (lead && Array.isArray(lead)) {
        contactsByRole.deal_team_lead = lead.map((contact: any) => ({
          value: contact[UserEntryFields.contacts__id] || '',
          label:
            contact[UserEntryFields.prog__display_name] ||
            `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
            contact[UserEntryFields.contacts__email] ||
            UNKNOWN_CONTACT,
          email: contact[UserEntryFields.contacts__email] || '',
          data: contact
        }))
      }

      setSelectedContacts(contactsByRole)
    }
  }, [dealRecord])

  return (
    <div
      data-debug='detail-tab-container'
      data-component='DetailTab'
      data-record-id={recordId}
      style={{ overflowY: 'auto', overflowX: 'hidden' }}
      className='h-full'
    >
      <Row gutter={12}>
        <Col span={8}>
          <DealOverviewSection
            formData={dealRecord}
            handleFieldChange={handleFieldChange}
            onPatchSuccess={onPatchSuccess}
            commitments={commitments}
            handleCommitmentChange={handleCommitmentChange}
            isDrawer={false}
            showCurrency={true}
            showTitle={true}
          />

          <div className='mt-3 space-y-2'>
            <RoundInformationSection
              formData={dealRecord}
              handleFieldChange={handleFieldChange}
              onPatchSuccess={onPatchSuccess}
              recordId={recordId}
            />
          </div>

          {showDealTeam && (
            <div className='mt-3 space-y-2'>
              <DealTeam
                dealId={recordId}
                recordId={recordId}
                formData={dealRecord}
                selectedContacts={selectedContacts}
                onContactsChange={setSelectedContacts}
                enableAutoPatch={!!recordId}
                className='mb-2'
              />
            </div>
          )}
        </Col>

        <Col span={16}>
          <Row gutter={12}>
            <Col span={12}>
              <CompanySection
                formData={dealRecord}
                handleFieldChange={handleFieldChange}
                onPatchSuccess={onPatchSuccess}
                recordId={recordId}
                getCompanyDetailsById={getCompanyDetailsById}
                cardProps={{ className: 'mb-2' }}
              />
            </Col>

            <Col span={12}>
              <BusinessAddressSection
                formData={dealRecord}
                handleFieldChange={handleFieldChange}
                onPatchSuccess={onPatchSuccess}
                recordId={recordId}
                cardProps={{ className: 'mb-2' }}
              />

              <RegisteredOfficeSection
                formData={dealRecord}
                handleFieldChange={handleFieldChange}
                onPatchSuccess={onPatchSuccess}
                recordId={recordId}
                cardProps={{ style: { marginTop: '16px' } }}
              />
            </Col>
          </Row>

          {showDealTeam && (
            <div className='mt-3'>
              <CompanyTeamTable
                key={companyTeamMembersKey || 'company-team-empty'}
                mode='existing'
                companyId={dealRecord?.[DealFields.deals__account]}
                initialTeamMembers={companyTeamMembers}
                onChange={handleTeamTableChange}
                readOnly={false}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  )
}
